"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = useObserverListener;
exports.useHasIntersectedWithElement = void 0;
exports.useInViewById = useInViewById;
var _react = require("react");
var _base = require("../base.functions");
const resolveFunctionalOptions = options => {
  for (const [option, value] of Object.entries(options)) {
    if (typeof value === 'function') {
      options[option] = value();
    }
  }
  return options;
};
function useObserverListener(targetSelectors, callback, intersectionObserverOptions = {
  root: null,
  rootMargin: '0px',
  threshold: 0
}) {
  const [observer, setObserver] = (0, _react.useState)(null);
  const [elements, setElements] = (0, _react.useState)([]);

  // Converts the options and targetSelectors to strings so that we can use them as proxy
  // dependencies in the useEffect hooks to prevent excessive re-runs on object re-renders.
  const staticOptions = JSON.stringify(resolveFunctionalOptions(intersectionObserverOptions));
  const staticTargetSelectors = JSON.stringify(targetSelectors);
  (0, _react.useEffect)(() => {
    if (elements.length > 0) {
      const newObserver = new IntersectionObserver(callback, JSON.parse(staticOptions));
      setObserver(newObserver);
    }
  }, [staticOptions, callback, elements]);
  (0, _react.useEffect)(() => {
    let selectors = JSON.parse(staticTargetSelectors);
    if (!Array.isArray(selectors)) {
      selectors = [selectors];
    }
    const resolvedElements = selectors.reduce((queryElements, selector) => [...queryElements, ...document.querySelectorAll(selector)], []);
    setElements(resolvedElements);
  }, [staticTargetSelectors]);
  (0, _react.useEffect)(() => {
    if (observer && elements.length > 0) {
      for (const element of elements) {
        observer.observe(element);
      }
    }
    return () => {
      observer?.disconnect();
    };
  }, [observer, elements]);
  return observer?.takeRecords;
}
function useInViewById(externalElementId, intersectionObserverOptions = {
  root: null,
  rootMargin: '0px',
  threshold: 0
}) {
  const [inView, setInView] = (0, _react.useState)(false);
  const [entry, setEntry] = (0, _react.useState)(null);
  const handleIntersect = (0, _react.useCallback)(entries => {
    const {
      isIntersecting
    } = entries[0];
    setInView(isIntersecting);
    setEntry(entries[0]);
  }, []);
  useObserverListener([`[id="${externalElementId}"]`], handleIntersect, intersectionObserverOptions);
  return {
    inView,
    entry
  };
}
const useHasIntersectedWithElement = (intersectedId, currentComponentRef = null, options = {}) => {
  const {
    debounceTime = 20,
    initialValue = false
  } = options;
  const [hasIntersected, setHasIntersected] = (0, _react.useState)(initialValue);
  const {
    inView,
    entry: inViewEntry
  } = useInViewById(intersectedId);
  (0, _react.useEffect)(() => {
    // scroll listener is not very performant on its own, so we debounce it and add it only when observedElement is in view
    const {
      target
    } = inViewEntry || {};
    if (!target) {
      return;
    }
    const handleScrollOverObserved = (0, _base.debounce)(() => {
      const {
        top
      } = target?.getBoundingClientRect() || {};
      if (top <= currentComponentRef?.current?.offsetHeight || 0) {
        setHasIntersected(true);
      } else {
        setHasIntersected(false);
      }
    }, debounceTime);
    if (inView) {
      document?.addEventListener('scroll', handleScrollOverObserved, {
        passive: true
      });
    } else {
      const {
        bottom
      } = target?.getBoundingClientRect() || {};
      if (bottom < currentComponentRef?.current?.offsetHeight || 0) {
        // if page loads under observed element
        setHasIntersected(true);
      }
      document?.removeEventListener('scroll', handleScrollOverObserved);
    }
    return () => document?.removeEventListener('scroll', handleScrollOverObserved);
  }, [inView, inViewEntry]);
  return hasIntersected;
};
exports.useHasIntersectedWithElement = useHasIntersectedWithElement;